// shadcn/ui components get vendored into src/components/ui/ as they're needed.
// The theme and design tokens live in src/theme.
// This file is the public surface of @marque/ui.
export { ResponsiveNavigation } from './ResponsiveNavigation'
export { PwaInstall } from './PwaInstall'

// AB#1846 — promise-based in-app dialogs replacing window.confirm / window.prompt / window.alert.
export { confirmDialog, promptDialog, alertDialog, DialogHost } from './v3/Dialogs'
// AB#2216 — reusable modal shell (arbitrary children), generalised from the DialogHost overlay.
export { Modal } from './v3/Modal'
export { FloatingPanel } from './v3/FloatingPanel'
export { AssistantLauncher } from './v3/AssistantLauncher'
export type { ModalProps } from './v3/Modal'
export type { V3EntityDef, V3FieldDef } from './v3/types'

// AB#1622 — the shared runtime view renderer (the same one the tenant uses) so the designer preview
// matches runtime.
// AB#2043 — EditField is the ONE type→control mapping; the tenant's injected editFieldComponent wraps it,
// adding only its data-coupled calendar-ref picker.
export { ViewRenderer, ListView, EditField, V3_INPUT_CLS } from './v3/ViewRenderer'
export { SpeechSettingsEditor, type SpeechProfileValue } from './v3/SpeechSettingsEditor'
export { GeneratedRecord, GeneratedList, GeneratedActions, ViewFeedback } from './v3/GeneratedNativeView'
export { GeneratedChat } from './v3/GeneratedChat'
// AB#3953 / AB#3954 — table column personalisation: the pure reconciliation (resolveColumns /
// serializeLayout), the table identity rule, the per-user layout hook with its store seam (the tenant
// supplies a preference-backed TableLayoutStore; the designer supplies none), and the configurator
// every shared table primitive renders.
export { tableIdentity, resolveColumns, serializeLayout, isStoredColumnLayout, isCustomisedLayout } from './v3/columnLayout'
export type { StoredColumnLayout } from './v3/columnLayout'
export { useTableLayout, useTableLayoutStore, TableLayoutStoreProvider, TABLE_LAYOUT_SAVE_DEBOUNCE_MS } from './v3/useTableLayout'
export type { TableLayoutStore, TableLayoutState } from './v3/useTableLayout'
export { useColumnConfigurator } from './v3/ColumnConfigurator'
export type { ColumnConfiguratorHandle, ColumnConfiguratorOptions } from './v3/ColumnConfigurator'
// AB#1866 — renderer seams: the tenant injects live custom/documents/EditField; designer uses defaults.
// AB#2040 — FetchEntityById is the searchable FK combobox's fetch-by-id label-resolver seam.
// AB#2049 — FormatContext threads the tenant locale + base currency into the shared value formatters.
// AB#2266 — FetchLookupOptions is the reference-data lookup option loader seam for lookupValue fields.
// AB#3967 — DrillDownScope is the company / site / valuation-area scope a metric drill-down carries to the host callback.
export type { ViewRendererProps, EditFieldProps, CustomSlotComponent, EditFieldComponent, FetchEntityOptions, FetchEntityById, FetchLookupOptions, FetchUnitOptions, FetchConnectionOptions, FetchUserOptions, FetchPreviewRows, FormatContext, DrillDownScope } from './v3/ViewRenderer'
export { planningWindowEnd, parsePlanningDate } from './v3/planningDates'
// AB#2040 — the async, loader-driven FK typeahead the shared LookupRefField renders (replacing the plain
// <select> that was capped at 200 rows). Exported so hosts/tests can reuse it directly.
// AB#2467 — the standalone address-composite editor, lifted out of the private AddressBlockSlot so
// hand-written pages (Company, Site) render the SAME editor as a module entity's address field.
export { AddressEditor, pairScalarRows } from './v3/AddressEditor'
export type { AddressEditorProps, FetchAddressSearch, AddressMapComponent } from './v3/AddressEditor'
// AB#4020 — the one scalar-coordinate reader (latField/lngField) shared by the address-block's initial-pin
// fallback and the tenant map-pin's deriveLatLng.
export { readScalarLatLng } from './v3/geoScalars'
export { countryAddressMeta, formatAddressLines } from './v3/addressMetadata'
export type { AddressValue, CountryAddressMeta } from './v3/addressMetadata'
export { AsyncLookupCombobox } from './v3/AsyncLookupCombobox'
export type { AsyncLookupComboboxProps, LookupOption } from './v3/AsyncLookupCombobox'
// AB#2598 — the host seam for creating a lookup option without leaving the form.
export { LookupCreateProvider, useLookupCreate } from './v3/lookupCreate'
export type { LookupCreateHandler, LookupCreateRequest } from './v3/lookupCreate'
export { UnitCatalogueProvider, useUnitCatalogue } from './v3/unitCatalogue'
export type { UnitOption } from './v3/unitCatalogue'
// AB#2652/AB#2654 — the host seam for metadata-sourced field options (another entity's statuses or
// field names as the option set).
export { EntityMetaProvider, useEntityMeta } from './v3/entityMeta'
export type { EntityMetaOptions, FetchEntityMeta } from './v3/entityMeta'
// AB#1610 — client-side computed-field evaluator for live line/total recalculation in editable grids.
export { evalFormula, applyComputes } from './v3/computeFormula'
// AB#2217 — the single entity-definition → renderer-metadata mapper, shared by the tenant and
// the designer preview so the two cannot drift on field labels, enum options or lookupFields.
export { buildEntityFieldMeta } from './v3/entityFieldMeta'
export { AssistantProfileEditor, type AssistantProfileValue } from './v3/AssistantProfileEditor'
export type { EntityFieldMetaSource, EntityFieldMetaFieldSource } from './v3/entityFieldMeta'
// AB#2412 — stored enum code → human display label (RUN_OUT → Run Out). Display only.
export { formatEnumLabel, enumOptions } from './v3/enumLabel'
export type { EnumOption, EnumOptionSource } from './v3/enumLabel'
// AB#3281 — the semantic status-tone vocabulary and its one resolution to theme classes.
export { STATUS_TONES, tonePillClasses, toneAccentBorderClasses, isStatusTone, resolveOptionTone } from './v3/statusTone'
export type { StatusTone } from './v3/statusTone'
// AB#3280 — the one responsive breakpoint + the tier context the renderer and shells read.
export { ViewportProvider, useViewport, COMPACT_BREAKPOINT_PX } from './v3/viewport'
export type { ViewportTier } from './v3/viewport'
// AB#3966 — the cockpit grid engine: pure 24-column layout functions (place / resize / remove /
// compact / first free slot / metric bands) the personal cockpit page runs every edit through.
export {
    COCKPIT_COLUMNS, collides, sortLayout, cloneLayout, hasOverlap, compact, placeWidget, resizeWidget,
    removeWidget, firstFreeSlot, sameLayout, bandsFor, neighbourInOrder,
} from './v3/cockpit/cockpitGrid'
export type { GridItem, GridSize, GridLimits, GridBand, GridPosition } from './v3/cockpit/cockpitGrid'
// AB#2387 — facet enforcement for the shared renderer's save path.
export { validateFieldFacets, validateDraftFacets } from './v3/validateFacets'
export type { FacetViolation } from './v3/validateFacets'
// AB#2382 — the shared field help affordance (info glyph + tooltip) used at every field-label site.
export { FieldHint } from './v3/FieldHint'
export type { FieldHintProps } from './v3/FieldHint'
// AB#2790 — the save-failure banner that scrolls itself into view and announces itself. Every editable
// view puts its Save at the bottom and its errors at the top, so a refusal has to come and find the user.
export { SaveErrorBanner } from './v3/SaveErrorBanner'
export type { SaveErrorBannerProps } from './v3/SaveErrorBanner'
// AB#1860 — static-text token interpolation.
export { interpolateTokens } from './v3/interpolate'
// AB#1862 — field-slot showIf normaliser (string expr or {field,eq,…} object).
// AB#2774 — the VIEW/ACTION showIf evaluator: accepts either authored form (a string expression or the
// documented {field,eq,neq,in,isSet} object) and understands `permission:CODE`. Since AB#2864 removed
// the legacy EntityScreen renderer and its older ShowIfCondition evaluator, this is THE showIf
// evaluator; the alias survives because the tenant imports it under this name.
export { evaluateShowIf as evaluateViewShowIf } from './v3/evaluateShowIfExpr'
// AB#1862 — the canonical V3 view-definition types live here; the designer + tenant re-export them
// (aliased to their local names) so there is one source of truth for the served-view contract.
export type {
    ViewDefinition as RuntimeViewDefinition,
    ViewComponent as RuntimeViewComponent,
    ViewComponentType as RuntimeViewComponentType,
    ViewContext as RuntimeViewContext,
    ViewContextEntry as RuntimeViewContextEntry,
    ViewGrid as RuntimeViewGrid,
    ViewAction as RuntimeViewAction,
    ViewActionShowIf as RuntimeViewActionShowIf,
    FieldSlot as RuntimeFieldSlot,
    FieldSectionField as RuntimeFieldSectionField,
    RelatedListPrefill as RuntimeRelatedListPrefill,
    TableColumn as RuntimeTableColumn,
    EntityFieldMeta as RuntimeEntityFieldMeta,
    ViewFieldMeta as RuntimeViewFieldMeta,
} from './v3/viewTypes'

// AB#1530 / AB#1560 — shared Integration Endpoint editor used by the platform designer and the
// Extension Studio tenant-authoring page (same surface, different host wiring).
export { IntegrationEndpointEditor, emptyEndpointDefinition, setEndpointAsync } from './integrations/IntegrationEndpointEditor'
export { SUSPENDING_STAGE_TYPES, findSuspendingStages, describeSuspendingStage, type SuspendingStageHit, type SuspendingStageSource, type SubProcessStagesLoader } from './integrations/suspendingStages'
export type {
    EndpointDefinition as IntegrationEndpointDefinition,
    Contract as IntegrationEndpointContract,
    ContractField as IntegrationEndpointContractField,
    IntegrationEndpointEditorProps,
} from './integrations/IntegrationEndpointEditor'

// AB#1659 — shared lucide icon registry + renderer (designer picker + tenant action runtime).
export { Icon, resolveIcon, LUCIDE_ICON_MAP, LUCIDE_ICON_NAMES } from './icons'

// AB#2041 — imperative toast primitive: `toast.error('…')` anywhere, <ToastHost /> mounted once per
// app in its persistent layout (the DialogHost pattern). Auto-dismisses after ~5s; manual dismiss.
export { toast, dismissToast, clearToasts, TOAST_DURATION_MS } from './toast/toastStore'
export type { ToastItem, ToastVariant, ToastOptions, ToastAction } from './toast/toastStore'
export { ToastHost } from './toast/ToastHost'

// AB#1690 — the shared grid Document Template editor + model, used by the platform process-designer
// and the tenant-embedded document designer (same surface, different host wiring).
export { DocumentTemplateEditor } from './v3/DocumentTemplateEditor'
export type { DocumentTemplateEditorProps, DocumentFieldSelect, DocumentFieldSelectProps } from './v3/DocumentTemplateEditor'
export {
    emptyTemplate, parseTemplateDefinition, PAGE_DIMENSIONS_MM, DEFAULT_MARGINS,
} from './v3/templateTypes'
export type {
    TemplateDefinition, TemplateComponent, TemplateComponentType, TemplateBinding,
    TemplateStyle, TemplateTableColumn, TemplateGrid, TemplatePage, PageMargins,
    PageSize, BarcodeSymbology, TextAlign, LineOrientation,
} from './v3/templateTypes'
// AB#1647 — custom view components: shared contract + build-time manifest.
export { CUSTOM_COMPONENT_MANIFEST } from './v3/customComponents'
export type {
    CustomComponentProps, CustomComponentManifestEntry,
    CustomComponentPropSchema, CustomComponentEventSchema,
} from './v3/customComponents'
// AB#2269 — material readiness chips: shared presentation, host-threaded loader seam (the tenant
// wires GET /api/v3/materials/{id}/readiness; the designer wires nothing → renders nothing).
export { ReadinessChips } from './v3/ReadinessChips'
export type { ReadinessChipsProps, ReadinessGate, ReadinessResult, LoadMaterialReadiness } from './v3/ReadinessChips'
// AB#1910 — currency codes for a currency-type field select (shared by the tenant's injected EditField).
export { CURRENCIES, CURRENCY_OPTIONS, currencyLabel } from './v3/currencies'
export type { CurrencyOption } from './v3/currencies'
export { COUNTRIES, type Country } from './v3/countries'
// AB#1925/1930 — month-number names (read mode) + picker source (edit mode), shared with the tenant EditField.
export { MONTHS, monthName } from './v3/months'

export const __placeholder = true;
export { toneChartColors } from './v3/statusTone'
export { readDataField } from './v3/readDataField'
export { TonePicker } from './v3/TonePicker'

import { useEffect, useRef } from 'react'

/**
 * Fires {@link handler} when the Escape key is pressed anywhere on the document.
 * Designed for modal dialogs — the handler ref pattern means you never need to
 * wrap the callback in `useCallback`; the effect attaches exactly once on mount.
 */
export function useEscapeKey(handler: () => void): void {
    const ref = useRef(handler)
    useEffect(() => { ref.current = handler })
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') ref.current()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [])
}
export { instantInputValue, instantStoredValue } from './v3/dateTimeInstant'
export { DataWorkspace } from './v3/DataWorkspace'
export type { WorkspaceDefinition, WorkspaceAction, WorkspaceBlock, WorkspaceColumn, WorkspacePage, WorkspaceEnvironment } from './v3/workspaceTypes'
export { workspaceValue, workspaceDatasets, workspaceRows, safeWorkspaceHref } from './v3/workspaceData'
export { recordWorkspaceLayout } from './v3/recordWorkspaceLayout'
export { SearchField } from './v3/SearchField'
export { V3_SECONDARY_BUTTON_CLS } from './v3/controlStyles'
export { RecordFinder } from './v3/RecordFinder'

export type { ChatDefinition } from './v3/chatTypes'
